import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import T from 'i18n-react';
import { ProgressButton } from '../../../core/Button';
import { fetchJsonData, getErrorMessageByStatus } from '../../../core/ajax';
import { API_USER_CERTIFICATE } from '../../routing';
import UploadButton from './UploadButton';
import useIsMounted from 'ismounted';
import { CERTIFICATE_STATUSES } from '../../../core/variables';
import makeStyles from '@material-ui/core/styles/makeStyles';
import imageCompression from 'browser-image-compression';
import { CERTIFICATE_TYPE } from '../../constants';

const TYPES = Object.values(CERTIFICATE_TYPE);
const requireBackImage = {
  driver_license: true,
  my_number: false,
  passport: true,
  residence_card: false,
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    margin: 16,
    [theme.breakpoints.up('sm')]: {
      margin: 32,
    },
  },
  description: {
    fontSize: theme.typography.pxToRem(15),
  },
  containerType: {
    paddingTop: '0.5em',
  },
  containerProgressButton: {
    '& > div': {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
  },
  containerActionButtons: {
    padding: '1em 0.5em',
  },
  actionButton: {
    maxWidth: 200,
  },
}));

const maxFileSize = 8000000;

const useImage = (snackbarRef) => {
  const [image, setImage] = React.useState();
  const isMounted = useIsMounted();
  const onSuccess = (file, dataUrl) => isMounted.current && setImage(dataUrl);
  const onError = () =>
    isMounted.current &&
    snackbarRef.current?.handleOpen(
      T.translate('user.certificates.failedGettingImage'),
    );
  const setBase64 = (file) => {
    if (!file) return;
    if (file && file.size > maxFileSize) {
      snackbarRef.current?.handleOpen(
        T.translate('imageUpload.tooLarge', { size: '8MB' }),
        'error',
      );
      return;
    }

    imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1000,
    })
      .then((file) => {
        imageCompression
          .getDataUrlFromFile(file)
          .then((dataUrl) => isMounted.current && onSuccess(file, dataUrl))
          .catch(onError);
      })
      .catch(onError);
  };
  return [image, setBase64];
};

const CertificateDialog = ({ open, setOpen, setStatus, snackbarRef }) => {
  const classes = useStyles();
  const [certificateType, setCertificateType] = React.useState(TYPES[0]);
  const [progress, setProgress] = React.useState(false);
  const [frontImage, setFrontImage] = useImage(snackbarRef);
  const [backImage, setBackImage] = useImage(snackbarRef);
  const isMounted = useIsMounted();

  const isUploadReady = requireBackImage[certificateType]
    ? frontImage && backImage
    : !!frontImage;

  const handleSubmit = () => {
    if (
      frontImage == null ||
      (requireBackImage[certificateType] && backImage == null)
    ) {
      snackbarRef.current?.handleOpen(
        T.translate('user.certificates.errorNotUploaded'),
        'error',
      );
      return;
    }
    if (!window.confirm(T.translate('user.certificates.confirmUploading'))) {
      return;
    }
    const data = {
      certificate_type: certificateType,
      certificate_front_blob: frontImage,
      certificate_back_blob: requireBackImage[certificateType] && backImage,
    };
    setProgress(true);
    fetchJsonData(
      API_USER_CERTIFICATE,
      {
        method: 'PATCH',
        credential: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: data }),
      },
      () => {
        if (isMounted.current) {
          snackbarRef.current?.handleOpen(
            T.translate('user.certificates.successUpload'),
            'success',
          );
          setOpen(false);
          setStatus(CERTIFICATE_STATUSES.CONFIRMING);
          setProgress(false);
        }
      },
      (response, data) => {
        if (isMounted.current) {
          snackbarRef.current?.handleOpen(
            getErrorMessageByStatus(response, data, 'post-certificate-error'),
            'error',
          );
          setProgress(false);
        }
      },
    );
  };

  return (
    <Dialog
      open={open}
      disableBackdropClick
      onBackdropClick={() => setOpen(false)}
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>{T.translate('user.certificates.title')}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText className={classes.description}>
          {T.translate('user.certificates.description')}
        </DialogContentText>
        <DialogContentText className={classes.description}>
          {T.translate('user.certificates.annotation')}
        </DialogContentText>
        <Grid container spacing={2} className={classes.containerType}>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="certificate-type">
                {T.translate('user.certificate')}
              </InputLabel>
              <Select
                labelId="certificate-type"
                id="certificate-type"
                value={certificateType}
                onChange={(e) => setCertificateType(e.target.value)}
                label={T.translate('user.certificate')}
              >
                {TYPES.map((type) => (
                  <MenuItem key={`certificate-` + type} value={type}>
                    {T.translate('user.certificateTypes.' + type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <DialogContentText>
              {T.translate('user.certificates.notes.' + certificateType)}
            </DialogContentText>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} container justify="center">
            <UploadButton
              image={frontImage}
              handleChange={(e) => setFrontImage(e.target.files[0])}
              side="front"
            />
          </Grid>
          {requireBackImage[certificateType] && (
            <Grid item xs={12} sm={6} container justify="center">
              <UploadButton
                image={backImage}
                handleChange={(e) => setBackImage(e.target.files[0])}
                side="back"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions className={classes.containerActionButtons}>
        <Grid container>
          <Grid item xs={6} container justify="center">
            <Button
              disabled={progress}
              fullWidth
              onClick={() => setOpen(false)}
              className={classes.actionButton}
            >
              {T.translate('close')}
            </Button>
          </Grid>
          <Grid
            item
            xs={6}
            container
            justify="center"
            className={classes.containerProgressButton}
          >
            <ProgressButton
              progress={progress}
              disabled={progress || !isUploadReady}
              fullWidth
              disableElevation
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              className={classes.actionButton}
            >
              {T.translate('offer')}
            </ProgressButton>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

CertificateDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  setStatus: PropTypes.func,
  snackbarRef: PropTypes.object,
};

export default CertificateDialog;
